import { defineStore } from 'pinia';
import { fetchPlans } from '@/views/plan/api/request';
import type { LoadedPlanStoreState, PlanStoreState } from '@/views/plan/store/types';
import { initialState } from '@/views/plan/store/initialState';
import { downloadPdf } from '@/views/plan/api/downloadPdf';
import { verify } from '@/util/verify';
import { downloadModel } from '@/views/plan/api/downloadModels';
import assert from 'assert';
import { taggedLogger } from '@/util/taggedLogger';
import type { ApiPlan, ApiPlans } from '@/views/plan/api/types';
import { pollPlans } from '@/views/plan/api/pollPlans';
import { computed, reactive, ref } from 'vue';
import { cloneDeep } from 'lodash';
import { useAppErrorStore } from '@/stores/appErrorStore';
import { alignmentMode } from '@/api/alignmentMode';
import { isPlanApprovalState } from '@/domain/plan/planApprovalState';
import { assertIs } from '@/util/assert';

const log = taggedLogger('plan-store');

function plansState(): PlanStoreState {
    return {
        ...cloneDeep(initialState),
    };
}

/**
 * Polling the plans endpoint into the store, and stopping again.
 *
 * Lifted out of the store body because the controller it owns is state of its own: a second start
 * has to abort the first, and the store has no other reason to hold an AbortController.
 */
function plansSync(state: PlanStoreState, appError: ReturnType<typeof useAppErrorStore>) {
    let controller: AbortController | null = null;

    return {
        start(caseId: number): void {
            log.info(`Start syncing plan ${caseId}...`);

            controller?.abort();
            const active = new AbortController();
            controller = active;

            appError.catchErrors(() =>
                pollPlans(
                    caseId,
                    (data: ApiPlans | null) => {
                        if (!data) {
                            throw new Error('Failed to load plan');
                        }
                        Object.assign(state, assignPlansDataToState(data));
                    },
                    { signal: active.signal },
                ),
            );
        },

        stop(): void {
            log.info('Stop syncing plans...');
            controller?.abort();
        },
    };
}

export const usePlansStore = defineStore('plans-store', () => {
    const appError = useAppErrorStore();

    const _state = reactive<PlanStoreState>(plansState());

    const isLoaded = computed(() => {
        return _state?.project !== null;
    });

    const caseId = ref<number | null>(null);

    const load = async (id: number) => {
        caseId.value = id;
        _clearState();

        log.info(`Loading plan for case ${caseId.value}...`);

        const data = await fetchPlans(id);
        if (!data) {
            throw new Error('Failed to load plan');
        }
        Object.assign(_state, assignPlansDataToState(data));

        await _startSync(id);
    };

    const _clearState = () => {
        log.info(`Clearing plan state for case ${caseId.value}...`);
        Object.assign(_state, plansState());
    };

    const sync = plansSync(_state, appError);

    const _startSync = async (id: number) => sync.start(id);

    const stopSync = () => sync.stop();

    const downloadAutomatedPlan = async () => {
        const report = verify(_state.automatedPlan?.report, 'automated report');
        await downloadPdf(report.href, report.name);
    };

    const downloadManualPlan = async () => {
        const report = verify(_state.manualPlan?.report, 'manual report');
        await downloadPdf(report.href, report.name);
    };

    // Return the state, getters, and actions
    return {
        state: _state as LoadedPlanStoreState,
        isLoaded,
        load,
        stopSync,
        downloadAutomatedPlan,
        downloadManualPlan,
        downloadModels,
        alignmentMode: computed(() => {
            const cupAlignMode = _state.specifications?.cup_align_mode;
            if (cupAlignMode) {
                return alignmentMode(cupAlignMode);
            }

            return null;
        }),
    };
});

/**
 * A plan whose approval state the domain recognises, or null when there is no such plan.
 *
 * Checked here, once per fetch, because the pages downstream decide from this state whether a
 * surgical guide may be ordered. A value the domain does not know would otherwise be compared
 * against 'approved' in a view and quietly read as "not approved".
 */
function checkedPlan(plan: ApiPlan | null | undefined): ApiPlan | null {
    if (!plan) {
        return null;
    }

    assertIs(isPlanApprovalState, plan.approval_state);

    return plan;
}

export function assignPlansDataToState(data: ApiPlans): LoadedPlanStoreState {
    return {
        project: verify(data.case, 'no case'),
        study: verify(data.study, 'no study'),
        specifications: verify(data.specifications, 'no specifications'),
        automatedPlan: checkedPlan(data.automated_plan),
        manualPlan: checkedPlan(data.manual_plan),
    };
}

/**
 * Note: the files are available for download only by the super-admin, admin and organization admin,
 * so it is likely that the files collection will be empty for the common users.
 */
const downloadModels = async (plan: ApiPlan) => {
    try {
        if (plan.files.length) {
            await downloadModel(plan.files, `Formus Case ${plan.reference_number} 3D Models.zip`);
        } else {
            log.error('Case not available for download');
        }
    } catch (err: unknown) {
        assert.ok(err instanceof Error);
        // TODO: provide the error information back to the user
        log.error('Failed to download 3D models: %s', err.message);
    }
};
